// ExtractionSummaryBar - "100 items · 24 extracted · 75 extracting · 1
// unsupported" for a WHOLE project (every upload source), at the top of the
// Files tab. The counts are drill-downs: clicking one lists the files it
// counts, and changes nothing about the lists below.
//
// ITEMS, not files. A row here is whatever the platform ingested, and an email
// THREAD is one row standing for a whole conversation rather than a file - on a
// mail-heavy project most rows are threads and their attachments, so "files"
// overstates what an operator can go and open. What the items ARE is broken down
// by the section headers below, which count the lists they sit above; this bar
// deliberately does not restate that from its own (differently filtered) payload.
import { useEffect, useState } from "react";

import type { UploadRecord } from "../api";
import { displayUploadName } from "../utils/uploadDisplayName";
import { SightingList } from "./SightingList";
import { SELECTABLE_LABEL_STYLE, isTextSelectionClick } from "./selectableButton";
import { Chevron } from "./Chevron";

interface Props {
  uploads: UploadRecord[] | undefined;
}

// "pending" covers both queued-for-extraction and actively-extracting (the BE
// reports the OCR pre-warm as "running" but an operator reads them as one
// state). "unsupported" is terminal: the content can't be extracted at all.
type ExtractionStateKey = "succeeded" | "pending" | "unsupported" | "failed";

// The states that name a PROBLEM needing an operator decision, and the only
// ones worth drilling into; the other counts stay plain text.
const PROBLEM_STATES = ["unsupported", "failed"] as const;
type ProblemStateKey = (typeof PROBLEM_STATES)[number];

const STATE_COLOR: Record<ExtractionStateKey, string> = {
  succeeded: "#16a34a",
  pending: "#ca8a04",
  unsupported: "#6b7280",
  failed: "#b03030",
};

const PROBLEM_COUNT_LABEL: Record<ProblemStateKey, string> = {
  unsupported: "– {n} unsupported",
  failed: "✗ {n} failed",
};

const PROBLEM_PANEL_LABEL: Record<ProblemStateKey, string> = {
  unsupported: "unsupported / unreadable",
  failed: "extraction failed",
};

interface ExtractionSummary {
  total: number;
  succeeded: number;
  pending: number;
  // Pending threads held until the mailbox import stops adding to them - waiting,
  // not extracting.
  held: number;
  // The problem states carry their FILES, not just a count, because the
  // drill-down panel lists them and the count is then just their length - one
  // pass, and the headline can't disagree with the panel under it.
  problems: Record<ProblemStateKey, UploadRecord[]>;
}

// Alphabetical by the name the panel actually SHOWS (so the order matches what
// the operator reads, not the stored "<subject> [<thread_id>].json"), matching
// the case-insensitive collation the sibling file lists use - the Drive tree's
// sortedFiles and the Gmail tree's subject sort. ``upload_id`` breaks ties so
// two same-named files can't swap places between renders.
const byDisplayName = (a: UploadRecord, b: UploadRecord): number =>
  displayUploadName(a.original_name).localeCompare(
    displayUploadName(b.original_name),
    undefined,
    { sensitivity: "base" },
  ) || a.upload_id.localeCompare(b.upload_id);

// Tombstoned (removed) and quarantined rows keep their stale extraction status
// and are hidden from the lists below, so counting them would leave the
// headline disagreeing with everything the operator can see.
const summarise = (uploads: UploadRecord[]): ExtractionSummary => {
  const summary: ExtractionSummary = {
    total: 0,
    succeeded: 0,
    pending: 0,
    held: 0,
    problems: { unsupported: [], failed: [] },
  };
  for (const u of uploads) {
    if (u.status === "removed" || u.status === "quarantined") continue;
    summary.total += 1;
    if (u.extraction_status === "succeeded") summary.succeeded += 1;
    // "skipped" is the retired spelling of "unsupported" and can still arrive
    // from old code during a deploy - bucket it with what it means, or it counts
    // as "extracting" for ever.
    else if (
      u.extraction_status === "unsupported" ||
      u.extraction_status === "skipped"
    )
      summary.problems.unsupported.push(u);
    else if (u.extraction_status === "failed") summary.problems.failed.push(u);
    else if (u.extraction_not_before) summary.held += 1;
    else summary.pending += 1;
  }
  for (const state of PROBLEM_STATES) summary.problems[state].sort(byDisplayName);
  return summary;
};

const Count = ({
  state,
  children,
}: {
  state: ExtractionStateKey;
  children: React.ReactNode;
}) => <span style={{ color: STATE_COLOR[state] }}>{children}</span>;

// A problem count that opens its drill-down. Rendered as a bordered pill so it
// reads as pressable next to the plain-text counts beside it - without a border
// the only cue would be the cursor, which nobody sees before they click.
const ProblemCount = ({
  state,
  count,
  expanded,
  panelId,
  onToggle,
}: {
  state: ProblemStateKey;
  count: number;
  expanded: boolean;
  panelId: string;
  onToggle: () => void;
}) => (
  <button
    type="button"
    aria-expanded={expanded}
    aria-controls={expanded ? panelId : undefined}
    title={
      expanded ? "Hide these files" : "Click to list the files in this state"
    }
    onClick={(e) => {
      // The label is a stat an operator may want to drag-select along with the
      // plain counts beside it, so it opts back into text selection - which
      // means a selection drag ending here also arrives as a click.
      if (isTextSelectionClick(e)) return;
      onToggle();
    }}
    style={{
      ...SELECTABLE_LABEL_STYLE,
      cursor: "pointer",
      font: "inherit",
      color: STATE_COLOR[state],
      background: expanded ? "#fff" : "transparent",
      border: `1px solid ${expanded ? STATE_COLOR[state] : "#d1d5db"}`,
      borderRadius: 999,
      padding: "1px 8px",
    }}
  >
    <Chevron tree open={expanded} />{" "}
    {PROBLEM_COUNT_LABEL[state].replace("{n}", String(count))}
  </button>
);

const ProblemPanel = ({
  state,
  files,
  panelId,
}: {
  state: ProblemStateKey;
  files: UploadRecord[];
  panelId: string;
}) => (
  <div
    id={panelId}
    style={{
      border: `1px solid ${STATE_COLOR[state]}`,
      borderRadius: 6,
      padding: "8px 12px",
      marginTop: 8,
      fontSize: 12,
    }}
  >
    <strong style={{ color: STATE_COLOR[state] }}>
      {files.length} file{files.length === 1 ? "" : "s"} -{" "}
      {PROBLEM_PANEL_LABEL[state]}
    </strong>
    <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
      {files.map((f) => (
        <li key={f.upload_id} style={{ marginBottom: 2 }}>
          {displayUploadName(f.original_name)}
          {/* Provenance through the one shared renderer, as every other panel
              that lists uploads does - a bare filename doesn't tell the
              operator which of the three sources to go and fix. */}
          <span style={{ color: "#9ca3af" }}>
            {" "}
            <SightingList sightings={f.sightings ?? []} />
          </span>
        </li>
      ))}
    </ul>
  </div>
);

export const ExtractionSummaryBar = ({ uploads }: Props): JSX.Element | null => {
  // One panel at a time: the two lists answer the same question about
  // different files, so stacking both just pushes the file lists off screen.
  const [openState, setOpenState] = useState<ProblemStateKey | null>(null);
  const summary = summarise(uploads ?? []);
  // Close the panel when its list empties, or the pill reappears already
  // expanded the next time a file lands in that state.
  const openEmptied = openState !== null && summary.problems[openState].length === 0;
  useEffect(() => {
    if (openEmptied) setOpenState(null);
  }, [openEmptied]);
  if (summary.total === 0) return null;
  const panelId = (state: ProblemStateKey) => `extraction-summary-${state}`;
  return (
    <div style={{ marginBottom: 16 }}>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 16,
          alignItems: "center",
          padding: "8px 12px",
          background: "#f8fafc",
          border: "1px solid #e5e7eb",
          borderRadius: 6,
          fontSize: 12,
          color: "#374151",
        }}
      >
        <span title="A file, or an email thread standing for a whole conversation.">
          <strong>{summary.total}</strong> item{summary.total === 1 ? "" : "s"} on
          this project
        </span>
        <Count state="succeeded">✓ {summary.succeeded} extracted</Count>
        {summary.pending > 0 && (
          <Count state="pending">⏳ {summary.pending} extracting</Count>
        )}
        {summary.held > 0 && (
          <Count state="pending">
            ⏸ {summary.held} waiting for the mailbox import
          </Count>
        )}
        {/* A problem state with no files isn't rendered at all, which is also
            what keeps a zero count from offering an empty drill-down. */}
        {PROBLEM_STATES.filter((state) => summary.problems[state].length > 0).map(
          (state) => (
            <ProblemCount
              key={state}
              state={state}
              count={summary.problems[state].length}
              expanded={openState === state}
              panelId={panelId(state)}
              onToggle={() =>
                setOpenState((cur) => (cur === state ? null : state))
              }
            />
          ),
        )}
      </div>
      {openState !== null && summary.problems[openState].length > 0 && (
        <ProblemPanel
          state={openState}
          files={summary.problems[openState]}
          panelId={panelId(openState)}
        />
      )}
    </div>
  );
};
